import { Capacitor } from '@capacitor/core';
import { useToasts } from '@/stores/toastStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { translate } from '@/i18n/translations';

/** Toast shown when a rewarded ad can't be served (no fill / load error). Keeps
 *  the "watch ad" buttons from feeling broken when inventory is empty. */
function notifyNoAd(): void {
  const lang = useSettingsStore.getState().lang;
  useToasts.getState().push({
    kind: 'warn',
    title: translate(lang, 'ads.none_available'),
    body: translate(lang, 'ads.none_available_body'),
    ttlMs: 3000,
  });
}

const REWARDED = {
  android: 'ca-app-pub-4087536081150593/5527789307',
  ios: 'ca-app-pub-4087536081150593/3472808313',
};
const INTERSTITIAL = {
  android: 'ca-app-pub-4087536081150593/3470457821',
  ios: 'ca-app-pub-4087536081150593/9725864667',
};

type AdMobModule = typeof import('@capacitor-community/admob');
let mod: AdMobModule | null = null;
let initPromise: Promise<boolean> | null = null;

function platform(): string {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
}

/** True only inside the native shells. Web hides ad-gated UI. */
export function adsAvailable(): boolean {
  const p = platform();
  return p === 'ios' || p === 'android';
}

// Test ads (always fill) are used in dev AND when VITE_ADS_TEST=true — set the
// latter for TestFlight/internal builds so reviewers/testers see ads even
// before the real AdMob units start serving. Unset it for the App Store build.
const USE_TEST_ADS = import.meta.env.DEV || import.meta.env.VITE_ADS_TEST === 'true';

async function ensureInit(): Promise<boolean> {
  if (!adsAvailable()) return false;
  if (!initPromise) {
    initPromise = (async () => {
      try {
        mod = await import('@capacitor-community/admob');
        await mod.AdMob.initialize({ initializeForTesting: USE_TEST_ADS });
        return true;
      } catch (e) {
        console.warn('[ads] init failed', e);
        return false;
      }
    })();
  }
  return initPromise;
}

const rewardedId = () => (platform() === 'ios' ? REWARDED.ios : REWARDED.android);
const interstitialId = () =>
  platform() === 'ios' ? INTERSTITIAL.ios : INTERSTITIAL.android;

/**
 * Show a rewarded video. Resolves true ONLY when the user earns the reward
 * (watched to completion); false on dismiss/failure/web. Callers should grant
 * the server-side reward only when this returns true.
 */
export async function showRewarded(): Promise<boolean> {
  if (!(await ensureInit()) || !mod) { notifyNoAd(); return false; }
  // Split load vs show: a prepare failure means no ad is available (no fill /
  // network) → tell the user. A failure during show is usually a user dismiss
  // → stay silent (they chose not to finish).
  try {
    await mod.AdMob.prepareRewardVideoAd({ adId: rewardedId(), isTesting: USE_TEST_ADS });
  } catch (e) {
    console.warn('[ads] rewarded failed to load', e);
    notifyNoAd();
    return false;
  }
  try {
    const reward = await mod.AdMob.showRewardVideoAd();
    return !!reward;
  } catch (e) {
    console.warn('[ads] rewarded dismissed', e);
    return false;
  }
}

/** Show an interstitial. Best-effort: never throws, never blocks the flow. */
export async function showInterstitial(): Promise<void> {
  if (!(await ensureInit()) || !mod) return;
  try {
    await mod.AdMob.prepareInterstitial({ adId: interstitialId(), isTesting: USE_TEST_ADS });
    await mod.AdMob.showInterstitial();
  } catch (e) {
    console.warn('[ads] interstitial failed', e);
  }
}

// Frequency-capped interstitial on the match→home transition. Matches are short,
// so we show one only every Nth return and never the first of the session
// (the counter resets on app launch → first interstitial lands on the Nth match).
const INTERSTITIAL_EVERY = 10;
let matchReturns = 0;

/** Call when leaving the result screen back to Home. Shows an interstitial on
 *  every Nth match return; no-op otherwise / on web. */
export async function maybeInterstitialOnReturn(): Promise<void> {
  matchReturns += 1;
  if (matchReturns % INTERSTITIAL_EVERY === 0) {
    await showInterstitial();
  }
}
